import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, CheckCircle } from 'lucide-react';

const OAuthSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [status, setStatus] = useState('processing');

  useEffect(() => {
    const handleOAuthSuccess = async () => {
      const token = searchParams.get('token');
      const userId = searchParams.get('user_id');

      if (!token || !userId) {
        setStatus('error');
        setTimeout(() => navigate('/login?error=missing_token'), 2000);
        return;
      }

      try {
        // Fetch user data using the token
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/user/profile`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch user profile');
        }

        const userData = await response.json();
        
        // Login user
        login(userData, token);
        
        setStatus('success');
        
        // Store onboarding redirect for PrivateRoute enforcement
        if (userData.role === 'vendor' && userData.status === 'pending' && !userData.onboarding_completed) {
          const redirectPath = userData.registration_step < 5
            ? `/vendor/tax-registration?step=${userData.registration_step}`
            : '/vendor/subscription-hub';
          localStorage.setItem('onboarding_redirect', redirectPath);
        } else {
          localStorage.removeItem('onboarding_redirect');
        }
        
        // Redirect based on user role and registration status
        setTimeout(() => {
          if (userData.role === 'vendor' && userData.status === 'pending' && !userData.onboarding_completed) {
            const redirectPath = localStorage.getItem('onboarding_redirect') || '/vendor/tax-registration';
            navigate(redirectPath);
          } else {
            navigate('/dashboard');
          }
        }, 1500);
        
      } catch (error) {
        console.error('OAuth success handling error:', error);
        setStatus('error');
        setTimeout(() => navigate('/login?error=auth_failed'), 2000);
      }
    };

    handleOAuthSuccess();
  }, [searchParams, login, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center justify-center py-12">
          {status === 'processing' && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-blue-600 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Signing you in...</h2>
              <p className="text-gray-600">Please wait while we complete your login.</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Login Successful!</h2>
              <p className="text-gray-600">Redirecting to dashboard...</p>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <span className="text-2xl">!</span>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Failed</h2>
              <p className="text-gray-600">Redirecting to login...</p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OAuthSuccess;
